// PatientPanel.js — the patient view: everything a provider wants at a
// glance for ONE patient. Rendered by ExpandedPanel's patientColumn in
// both homes — the wide layout's right column and the narrow layout's
// PATIENT tab. Auto-follows the session unless pinned.
//
// Top to bottom it answers the clinician's questions in the order she'd
// ask them: who is this and where are they in the plan (history +
// plan-of-care progress), anything I must not miss (flags), what
// happened last time, what's today's visit and how is the note scoring,
// what does the agent want me to fix (this patient's nudges), what has
// it already done (receipts with undo) — then act: dictate, open chart.
import React, { useEffect, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { liveStore, scoreToGrade, QUEUE_KINDS } from 'agent/liveStore';
import { audioRouter, ROUTES } from 'agent/audioRouter';
import { T, card, pill, buttonPrimary, buttonGhost } from './liveTheme';
import { NudgeCard } from './ActionQueue';

// "not applicable": a conditional rule whose condition did not come up in
// this visit (COM-564). Neither a pass nor a miss, so grey, and listed last.
const statusColor = (status) =>
  status === 'fully met'
    ? T.greenBright
    : status === 'partially met'
      ? T.amber
      : status === 'not applicable'
        ? T.textFaint
        : T.redSoft;

const statusIcon = (status) =>
  status === 'fully met'
    ? 'fa-check'
    : status === 'partially met'
      ? 'fa-exclamation-circle'
      : status === 'not applicable'
        ? 'fa-minus'
        : 'fa-times';

const notApplicableLast = (evals) => [
  ...evals.filter((e) => e.status !== 'not applicable'),
  ...evals.filter((e) => e.status === 'not applicable'),
];

const Label = ({ children, style }) => (
  <p
    style={{
      margin: '0 0 2px',
      fontSize: 10,
      color: T.textFaint,
      letterSpacing: '.5px',
      ...style,
    }}
  >
    {children}
  </p>
);

const InfoRow = ({ label, children }) => (
  <p style={{ margin: '5px 0 0', fontSize: 11, lineHeight: 1.45 }}>
    <span style={{ color: T.textFaint, fontWeight: 600 }}>{label} </span>
    <span style={{ color: T.textSoft }}>{children}</span>
  </p>
);

/** History card — fields render defensively: the demo fixture is rich
 * (diagnosis / plan / goals / HEP / flags); the real /v1/patient/history
 * response may carry only some of these. */
const PatientHistoryCard = ({ history }) => {
  if (!history) return null;
  const hasPlan =
    Number.isFinite(history.visitsCompleted) &&
    Number.isFinite(history.plannedVisits) &&
    history.plannedVisits > 0;
  const any =
    history.diagnosis ||
    history.flags ||
    history.lastVisit ||
    history.goals ||
    history.hep ||
    hasPlan;
  if (!any) return null;
  return (
    <div style={{ ...card(), padding: 10 }}>
      <Label>PATIENT</Label>
      {history.diagnosis && (
        <p
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 600,
            color: T.textPrimary,
            lineHeight: 1.4,
          }}
        >
          {history.diagnosis}
        </p>
      )}
      {hasPlan && (
        <div style={{ marginTop: 7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10, color: T.textFaint }}>
              PLAN OF CARE
            </span>
            <span style={{ fontSize: 10, color: T.textSoft }}>
              {history.visitsCompleted} of {history.plannedVisits} visits
            </span>
          </div>
          <div
            style={{
              marginTop: 3,
              height: 4,
              borderRadius: 2,
              background: 'rgba(159,232,200,0.12)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.min(
                  100,
                  (history.visitsCompleted / history.plannedVisits) * 100,
                )}%`,
                background: T.gradient,
                borderRadius: 2,
              }}
            />
          </div>
        </div>
      )}
      {history.flags && (
        <p
          style={{
            margin: '7px 0 0',
            fontSize: 11,
            lineHeight: 1.45,
            color: T.amber,
          }}
        >
          <i
            className="fa fa-flag"
            aria-hidden="true"
            style={{ fontSize: 9 }}
          />{' '}
          {history.flags}
        </p>
      )}
      {history.lastVisit && (
        <InfoRow label="Last visit:">{history.lastVisit}</InfoRow>
      )}
      {history.goals && <InfoRow label="Goals:">{history.goals}</InfoRow>}
      {history.hep && <InfoRow label="HEP:">{history.hep}</InfoRow>}
    </div>
  );
};

const PatientPanel = ({ conversationId, pinned = true, onUnpin }) => {
  // apiClient comes from the provider so demo mode's canned data flows here.
  const { state, actions, apiClient } = useComprehendLive();
  const [details, setDetails] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const session = state.sessions.find(
    (s) => s.conversationId === conversationId,
  );
  const group = session?.group;

  useEffect(() => {
    if (!conversationId) return undefined;
    let cancelled = false;
    setLoading(true);
    setDetails(null);
    apiClient
      .getConversationDetails(conversationId)
      .then((data) => {
        if (!cancelled) {
          setDetails(data && !data.error ? data : null);
          setLoading(false);
        }
      })
      .catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [apiClient, conversationId]);

  useEffect(() => {
    setHistory(null);
    if (!group || typeof apiClient.getPatientHistory !== 'function')
      return undefined;
    let cancelled = false;
    apiClient
      .getPatientHistory(group)
      .then((h) => {
        if (!cancelled) setHistory(h && !h.error ? h : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [apiClient, group]);

  if (!session) {
    // Column mode with nothing to follow (empty schedule) — quiet hint.
    return (
      <p
        style={{
          margin: '18px 0',
          fontSize: 11,
          color: T.textFaint,
          textAlign: 'center',
        }}
      >
        No patient in focus — tap a visit or a nudge.
      </p>
    );
  }

  const dictating = state.dictateTargetId === conversationId;
  const nudges = state.queue.filter(
    (q) =>
      q.kind === QUEUE_KINDS.NUDGE &&
      q.conversationId === conversationId &&
      (q.status === 'pending' || q.status === 'working'),
  );
  const recentActivity = state.queue
    .filter(
      (q) => q.status === 'resolved' && q.conversationId === conversationId,
    )
    .slice(-3)
    .reverse();
  const evals = details?.requirement_evaluations || [];

  const toggleDictate = () => {
    if (dictating) {
      audioRouter.setRoute(ROUTES.SESSIONS);
      liveStore.setDictateTarget(null);
    } else {
      audioRouter.setRoute(ROUTES.DICTATE, { conversationId });
      liveStore.setDictateTarget(conversationId);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            color: T.textPrimary,
            fontSize: 12,
            fontWeight: 600,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {session.patientName || session.name}
        </span>
        {session.recording && (
          <>
            <span
              className="cpl-dot"
              style={{ background: T.red, width: 7, height: 7, flexShrink: 0 }}
            />
            <span style={{ color: T.redSoft, fontSize: 11, flexShrink: 0 }}>
              recording
            </span>
          </>
        )}
        {/* Whether the panel is tracking the session (FOLLOWING) or
            pinned to an explicit selection. */}
        {pinned ? (
          <span
            onClick={onUnpin}
            role="button"
            title="Unpin — follow the session again"
            style={{
              ...pill('rgba(159,232,200,0.12)', T.textSecondary),
              fontSize: 9,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <i className="fa fa-thumbtack" aria-hidden="true" /> PINNED ✕
          </span>
        ) : (
          <span
            title="Following your session — taps pin a patient here"
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.5px',
              color: T.textFaint,
              flexShrink: 0,
            }}
          >
            FOLLOWING
          </span>
        )}
        <i
          className="fa fa-external-link-alt"
          aria-hidden="true"
          onClick={() => actions.openVisit(conversationId)}
          style={{
            marginLeft: 'auto',
            color: T.textSecondary,
            fontSize: 12,
            cursor: 'pointer',
          }}
        />
      </div>

      <PatientHistoryCard history={history} />

      <div style={{ ...card(), padding: 10 }}>
        <Label>VISIT</Label>
        <p style={{ margin: 0, fontSize: 11, color: T.textSoft }}>
          {session.name}
          {details?.config ? ` · ${details.config}` : ''}
          {details?.seconds ? ` · ${Math.round(details.seconds / 60)} min` : ''}
        </p>
      </div>

      <div style={{ ...card(), padding: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 6,
          }}
        >
          <Label style={{ margin: 0 }}>
            {session.recording ? 'LIVE SCORE' : 'SCORE'}
          </Label>
          <span
            style={{
              marginLeft: 'auto',
              ...pill('rgba(251,191,36,0.16)', T.amber),
            }}
          >
            {scoreToGrade(details?.score)}
            {details?.score ? ` ${details.score}` : ''}
          </span>
        </div>
        {loading && (
          <p style={{ margin: 0, fontSize: 11, color: T.textSecondary }}>
            Loading breakdown…
          </p>
        )}
        {!loading && evals.length === 0 && (
          <p style={{ margin: 0, fontSize: 11, color: T.textSecondary }}>
            No compliance evaluation yet — it lands when the note is scored.
          </p>
        )}
        {notApplicableLast(evals).slice(0, 6).map((e, i) => (
          <p
            key={i}
            style={{
              margin: 0,
              fontSize: 11,
              lineHeight: 1.8,
              color: statusColor(e.status),
            }}
          >
            <i
              className={`fa ${statusIcon(e.status)}`}
              aria-hidden="true"
              style={{ fontSize: 10, width: 12 }}
            />{' '}
            {e.requirement_text}
          </p>
        ))}
        {!loading && evals.length > 0 && (
          <p style={{ margin: '6px 0 0', fontSize: 10, color: T.textFaint }}>
            ask: “why is this a {scoreToGrade(details?.score)}?”
          </p>
        )}
      </div>

      {nudges.length > 0 && (
        <>
          <Label style={{ margin: '10px 0 5px', fontWeight: 700 }}>
            NUDGES ({nudges.length})
          </Label>
          {nudges.map((n) => (
            <NudgeCard key={n.id} item={n} actions={actions} hidePatient />
          ))}
        </>
      )}

      {recentActivity.length > 0 && (
        <>
          <Label style={{ margin: '10px 0 3px', fontWeight: 700 }}>
            AGENT ACTIVITY
          </Label>
          {recentActivity.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '2px 2px',
                color: T.greenBright,
                fontSize: 11,
              }}
            >
              <i
                className="fa fa-check"
                aria-hidden="true"
                style={{ fontSize: 10 }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                {item.detail || item.title || 'Applied'}
              </span>
              {item.undo && (
                <span
                  onClick={() => actions.undoItem(item)}
                  style={{
                    color: T.textSecondary,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Undo
                </span>
              )}
            </div>
          ))}
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          style={{
            ...buttonPrimary,
            flex: 1,
            padding: '8px 0',
            background: dictating ? T.red : T.green,
            color: dictating ? '#fff' : T.greenDark,
          }}
          onClick={toggleDictate}
        >
          <i className="fa fa-microphone" aria-hidden="true" />{' '}
          {dictating ? 'Stop dictating' : 'Dictate to this visit'}
        </button>
        <button
          style={{ ...buttonGhost, flex: 1, padding: '8px 0' }}
          onClick={() => actions.openVisit(conversationId)}
        >
          <i className="fa fa-folder-open" aria-hidden="true" /> Open chart
        </button>
      </div>
      <p
        style={{
          margin: '8px 0 0',
          textAlign: 'center',
          fontSize: 10,
          color: T.textFaint,
        }}
      >
        dictate routes your mic to this visit only
      </p>
    </div>
  );
};

export default PatientPanel;
